import Box, { type BoxProps } from '@mui/material/Box';

import { useInView } from '@/hooks/useInView';

import { type RevealDirection, revealHiddenTransform, revealTransition } from './revealMotion';

export type { RevealDirection };

type Props = BoxProps & { delay?: number; direction?: RevealDirection };

/**
 * Fades content in as it scrolls into view (§52). Content is fully visible in
 * the prerendered HTML and without JavaScript; only elements that start below
 * the fold are hidden, and only after the page is interactive. Reduced-motion
 * visitors never see the effect.
 *
 * Only `opacity` and `transform` are animated, so every reveal stays on the
 * compositor and none of them can trigger layout.
 */
export function Reveal({ delay = 0, direction = 'up', sx, ...rest }: Props) {
  const { ref, state } = useInView();

  return (
    <Box
      ref={ref}
      sx={[
        state !== 'static' && {
          transition: revealTransition,
          transitionDelay: `${delay}ms`,
          willChange: 'opacity, transform',
        },
        state === 'waiting' && { opacity: 0, transform: revealHiddenTransform[direction] },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}
